import React, { useState, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { FaIdCard, FaBuilding, FaBriefcase, FaMapMarkerAlt, FaCalendarAlt, FaPhone, FaEnvelope, FaFileUpload, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { saveEmploymentDetails } from '../../../services/candidate/api';
import PopupModel from '../../popup-model/PopupModel';
import './NewEmployement.css';

const EmployeeDetailsForm = ({ employmentId }) => {
  const [formData, setFormData] = useState({
    employeeCode: '',
    companyName: '',
    jobTitle: '',
    location: '',
    workingFrom: '',
    workingTo: '',
    hrContactNumber: '',
    hrContactEmail: '',
    employerReferenceNumber: '',
    uanNumber: '',
  });
  const navigate = useNavigate();

  const [files, setFiles] = useState({
    experienceLetter: null,
    offerLetter: null,
    salarySlips: [],
    bankStatement: null,
    form16: null,
    form26: null,
  });

  const [loading, setLoading] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Create refs for input fields to manage focus
  const inputRefs = {
    employeeCode: useRef(null),
    companyName: useRef(null),
    jobTitle: useRef(null),
    location: useRef(null),
    workingFrom: useRef(null),
    workingTo: useRef(null),
    hrContactNumber: useRef(null),
    hrContactEmail: useRef(null),
    employerReferenceNumber: useRef(null),
    uanNumber: useRef(null),
  };

  // Define the order of input fields for Enter key navigation
  const inputOrder = [
    'employeeCode',
    'companyName',
    'jobTitle',
    'location',
    'workingFrom',
    'workingTo',
    'hrContactNumber',
    'hrContactEmail',
    'employerReferenceNumber',
    'uanNumber',
  ];

  // Handle Enter key press to move to the next input
  const handleKeyDown = (e, currentField) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      const currentIndex = inputOrder.indexOf(currentField);
      const nextIndex = currentIndex + 1;
      if (nextIndex < inputOrder.length) {
        const nextField = inputOrder[nextIndex];
        inputRefs[nextField].current.focus();
      }
    }
  };

  // Parse date string to Date object for comparison
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    try {
      if (dateStr.match(/^\d{4}$/)) {
        // Year-only format (YYYY)
        return new Date(parseInt(dateStr, 10), 0, 1);
      } else if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
        // Full date format (DD-MM-YYYY)
        const [day, month, year] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleDrop = (e, field) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (field === 'salarySlips') {
      const validFiles = [];
      const oversizedFiles = [];
      const invalidTypeFiles = [];
      droppedFiles.forEach((file) => {
        if (file.type !== 'application/pdf') {
          invalidTypeFiles.push(file.name);
        } else if (file.size > maxSize) {
          oversizedFiles.push(file.name);
        } else if (file.size > 0) {
          validFiles.push(file);
        }
      });

      let errorMessage = '';
      if (invalidTypeFiles.length > 0) {
        errorMessage = `Non-PDF files detected: ${invalidTypeFiles.join(', ')}`;
      } else if (oversizedFiles.length > 0) {
        errorMessage = `Files exceeding 5MB: ${oversizedFiles.join(', ')}`;
      }

      setValidationErrors((prev) => ({ ...prev, salarySlips: errorMessage }));

      if (validFiles.length > 0) {
        setFiles((prev) => ({
          ...prev,
          salarySlips: [...prev.salarySlips, ...validFiles].slice(0, 3),
        }));
        if (!errorMessage) {
          setValidationErrors((prev) => ({ ...prev, salarySlips: '' }));
        }
      }
    } else {
      const file = droppedFiles[0];
      if (file && file.type === 'application/pdf' && file.size > 0 && file.size <= maxSize) {
        setFiles((prev) => ({ ...prev, [field]: file }));
        setValidationErrors((prev) => ({ ...prev, [field]: '' }));
      } else {
        const errorMessage = file
          ? file.type !== 'application/pdf'
            ? `Only PDF files are allowed for ${field}`
            : `File ${file.name} exceeds 5MB`
          : `Only PDF files under 5MB are allowed for ${field}`;
        setValidationErrors((prev) => ({ ...prev, [field]: errorMessage }));
      }
    }
  };

  const handleFileChange = (e, field) => {
    const selectedFiles = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (field === 'salarySlips') {
      const validFiles = [];
      const oversizedFiles = [];
      const invalidTypeFiles = [];
      selectedFiles.forEach((file) => {
        if (file.type !== 'application/pdf') {
          invalidTypeFiles.push(file.name);
        } else if (file.size > maxSize) {
          oversizedFiles.push(file.name);
        } else if (file.size > 0) {
          validFiles.push(file);
        }
      });

      let errorMessage = '';
      if (invalidTypeFiles.length > 0) {
        errorMessage = `Non-PDF files detected: ${invalidTypeFiles.join(', ')}`;
      } else if (oversizedFiles.length > 0) {
        errorMessage = `Files exceeding 5MB: ${oversizedFiles.join(', ')}`;
      }

      setValidationErrors((prev) => ({ ...prev, salarySlips: errorMessage }));

      if (validFiles.length > 0) {
        setFiles((prev) => ({
          ...prev,
          salarySlips: [...prev.salarySlips, ...validFiles].slice(0, 3),
        }));
        if (!errorMessage) {
          setValidationErrors((prev) => ({ ...prev, salarySlips: '' }));
        }
      }
    } else {
      const file = selectedFiles[0];
      if (file && file.type === 'application/pdf' && file.size > 0 && file.size <= maxSize) {
        setFiles((prev) => ({ ...prev, [field]: file }));
        setValidationErrors((prev) => ({ ...prev, [field]: '' }));
      } else {
        const errorMessage = file
          ? file.type !== 'application/pdf'
            ? `Only PDF files are allowed for ${field}`
            : `File ${file.name} exceeds 5MB`
          : `Only PDF files under 5MB are allowed for ${field}`;
        setValidationErrors((prev) => ({ ...prev, [field]: errorMessage }));
      }
    }
  };

  const handleRemoveFile = (field, index = null) => {
    if (field === 'salarySlips') {
      setFiles((prev) => ({
        ...prev,
        salarySlips: prev.salarySlips.filter((_, i) => i !== index),
      }));
    } else {
      setFiles((prev) => ({ ...prev, [field]: null }));
    }
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'employeeCode':
        if (!value) {
          error = 'Employee code is required';
        }
        break;
      case 'companyName':
        if (!value) error = 'Company name is required';
        else if (!/^[a-zA-Z0-9 ]+$/.test(value)) {
          error = 'Special characters are not allowed';
        }
        break;
      case 'jobTitle':
        if (!value) error = 'Job title is required';
        else if (!/^[A-Za-z ]{1,50}$/.test(value)) error = 'Job title can only contain alphabets and spaces (max 50 characters)';
        break;
      case 'location':
        if (value && !/^[A-Za-z ,]{1,100}$/.test(value)) {
          error = 'Location can only contain alphabets, spaces, and commas (max 100 characters)';
        }
        break;

      case 'workingFrom':
        if (!value) {
          error = 'Working from date is required';
        } else if (!/^(\d{4}|\d{2}-\d{2}-\d{4})$/.test(value)) {
          error = 'Working from must be in DD-MM-YYYY or YYYY format';
        } else {
          const fromDate = parseDateString(value);
          const toDate = parseDateString(formData.workingTo);
          const today = new Date();
          if (fromDate && fromDate > today) {
            error = 'Working From date cannot be in the future.';
          }
          if (fromDate && toDate && fromDate > toDate) {
            error = 'Working From date cannot be after Working To date.';
          }
        }
        break;

      case 'workingTo':
        if (!value) {
          error = 'Working to date is required';
        } else if (!/^(\d{4}|\d{2}-\d{2}-\d{4})$/.test(value)) {
          error = 'Working to must be in DD-MM-YYYY or YYYY format';
        } else {
          const fromDate = parseDateString(formData.workingFrom);
          const toDate = parseDateString(value);
          const today = new Date();
          if (toDate && toDate > today) {
            error = 'Working To date cannot be in the future.';
          }
          if (fromDate && toDate && fromDate > toDate) {
            error = 'Working From date cannot be after Working To date.';
          }
        }
        break;

      case 'hrContactNumber':
        if (value && !/^\d{10}$/.test(value)) error = 'HR contact number must be 10 digits if provided';
        break;
      case 'hrContactEmail':
        if (!value) error = 'HR contact email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Valid HR contact email is required';
        break;
      case 'uanNumber':
        if (value && !/^\d{12}$/.test(value)) {
          error = 'UAN must be exactly 12 digits';
        }
        break;
      case 'employerReferenceNumber':
        if (value && !/^\d{10}$/.test(value)) {
          error = 'EmployerReferenceNumber must be exactly 10 digits';
        }
        break;
      default:
        break;
    }
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((key) => {
      const fieldError = validateField(key, formData[key]);
      if (fieldError) errors[key] = fieldError;
    });

    // Validate files
    if (!files.offerLetter) errors.offerLetter = 'Offer letter is required';
    if (!files.bankStatement) errors.bankStatement = 'Bank statement is required';
    if (files.salarySlips.length !== 3) errors.salarySlips = 'Exactly 3 salary slips are required';

    setValidationErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionResult(null);
    setShowResultPopup(false);

    if (!validateForm()) {
      setSubmissionResult({
        isSuccess: false,
        title: 'Validation Error',
        message: 'Please fix the errors in the form before submitting.',
      });
      setShowResultPopup(true);
      return;
    }

    setLoading(true);
    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });

    if (files.experienceLetter) data.append('experienceLetter', files.experienceLetter);
    if (files.offerLetter) data.append('offerLetter', files.offerLetter);
    if (files.bankStatement) data.append('bankStatement', files.bankStatement);
    if (files.form16) data.append('form16', files.form16);
    if (files.form26) data.append('form26', files.form26);
    files.salarySlips.forEach((file) => {
      data.append('salarySlips', file);
    });

    try {
      const response = await saveEmploymentDetails(data);
      //console.log('API Response:', JSON.stringify(response, null, 2));

      if (response.statusCode === 200 && response.status === true) {
        setSubmissionResult({
          isSuccess: true,
          title: 'Success',
          message: response.message || 'Employment details saved successfully!',
        });
        setFormData({
          employeeCode: '',
          companyName: '',
          jobTitle: '',
          location: '',
          workingFrom: '',
          workingTo: '',
          hrContactNumber: '',
          hrContactEmail: '',
          employerReferenceNumber: '',
          uanNumber: '',
        });
        setFiles({
          experienceLetter: null,
          offerLetter: null,
          salarySlips: [],
          bankStatement: null,
          form16: null,
          form26: null,
        });
        setValidationErrors({});
      } else {
        const errorMessage = response?.message || 'Failed to save employment details: Invalid response structure';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Submission Error:', {
        message: err.message,
        code: err.code,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers,
        formData: Object.fromEntries(data.entries()),
      });
      let errorMessage = err.response?.data?.message || err.message || 'Failed to save employment details';
      if (err.response?.status === 503) {
        errorMessage = 'The server is temporarily unavailable. Please try again later.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid data submitted. Please check your inputs and try again.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Unauthorized request. Please ensure you are logged in.';
      }
      setSubmissionResult({
        isSuccess: false,
        title: 'Submission Error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
      setShowResultPopup(true);
    }
  };

  const closeResultPopup = () => {
    setShowResultPopup(false);
    setSubmissionResult(null);
  };

  const renderUploadField = (label, fieldName) => (
    <div className="uploadField">
      <p className="upload-label-title">
        <FaFileUpload className="filed-icons" /> {label}
      </p>
      <div
        className="upload-certificate-button"
        onClick={() => document.getElementById(fieldName).click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => handleDrop(e, fieldName)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            document.getElementById(fieldName).click();
          }
        }}
        aria-describedby={`${fieldName}-description`}
      >
        {fieldName === 'salarySlips'
          ? `Drag & Drop or Click to Upload up to ${3 - files.salarySlips.length} PDFs`
          : 'Drag & Drop or Click to Upload PDF'}
      </div>
      <input
        type="file"
        id={fieldName}
        accept=".pdf"
        multiple={fieldName === 'salarySlips'}
        hidden
        onChange={(e) => handleFileChange(e, fieldName)}
        aria-describedby={`${fieldName}-description`}
      />
      <div className="upload-error-container">
        {validationErrors[fieldName] && (
          <Typography color="error" variant="caption" id={`${fieldName}-description`}>
            {validationErrors[fieldName]}
          </Typography>
        )}
        {(fieldName !== 'salarySlips' && files[fieldName]) && (
          <div className="file-list">
            <span className="file-list-item">
              {files[fieldName].name}
              <FaTimes
                className="remove-file-icon"
                onClick={() => handleRemoveFile(fieldName)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleRemoveFile(fieldName);
                  }
                }}
                aria-label={`Remove ${files[fieldName].name}`}
              />
            </span>
          </div>
        )}
        {fieldName === 'salarySlips' && files.salarySlips.length > 0 && (
          <div className="file-list">
            {files.salarySlips.map((file, index) => (
              <span key={index} className="file-list-item">
                {file.name}
                <FaTimes
                  className="remove-file-icon"
                  onClick={() => handleRemoveFile('salarySlips', index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleRemoveFile('salarySlips', index);
                    }
                  }}
                  aria-label={`Remove ${file.name}`}
                />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderInputField = (label, name, type = 'text', placeholder = '', Icon) => (
    <div className="inputField">
      <p className="upload-label-title">
        {Icon && <Icon className="filed-icons" />} {label}
      </p>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={(e) => handleKeyDown(e, name)}
        placeholder={placeholder}
        className={validationErrors[name] ? 'input-error' : ''}
        ref={inputRefs[name]}
      />
      {validationErrors[name] && (
        <Typography color="error" variant="caption">
          {validationErrors[name]}
        </Typography>
      )}
    </div>
  );

  return (
    <>
      <h2 className="candidate-titles">Employment Details</h2>
      <Box className="employee-form-container">
        {loading && (
          <Box className="loading-overlay">
            <Box className="loading-content">
              <CircularProgress size={40} style={{ color: '#4D0054' }} />
              <Typography className="loading-text">Saving...</Typography>
            </Box>
          </Box>
        )}
        <form className="employee-form" onSubmit={handleSubmit}>
          <div className="grid-2col">
            {renderInputField('Employee Code', 'employeeCode', 'text', '', FaIdCard)}
            {renderInputField('Company Name', 'companyName', 'text', '', FaBuilding)}
            {renderInputField('Job Title', 'jobTitle', 'text', '', FaBriefcase)}
            {renderInputField('Location(optional)', 'location', 'text', '', FaMapMarkerAlt)}
            {renderInputField('Working From* (DD-MM-YYYY or YYYY)', 'workingFrom', 'text', 'DD-MM-YYYY or YYYY', FaCalendarAlt)}
            {renderInputField('Working To* (DD-MM-YYYY or YYYY)', 'workingTo', 'text', 'DD-MM-YYYY or YYYY', FaCalendarAlt)}
            {renderInputField('HR Contact Number (10 digits)', 'hrContactNumber', 'tel', '', FaPhone)}
            {renderInputField('HR Contact Email*', 'hrContactEmail', 'email', '', FaEnvelope)}
            {renderInputField('Employer Reference / CoWorker Number (optional)', 'employerReferenceNumber', 'text', '', FaIdCard)}
            {renderInputField('Universal Account Number(UAN) (optional)', 'uanNumber', 'text', '', FaIdCard)}
          </div>
          <div className="grid-2col file-grid">
            {renderUploadField('Experience Letter (PDF Only)(optional)', 'experienceLetter')}
            {renderUploadField('Offer Letter (PDF Only)', 'offerLetter')}
            {renderUploadField('Salary Slips (Exactly 3 PDFs)', 'salarySlips')}
            {renderUploadField('Bank Statement (PDF Only)', 'bankStatement')}
            {renderUploadField('Form 16 (PDF Only)(optional)', 'form16')}
            {renderUploadField('Form 26 (PDF Only)(optional)', 'form26')}
          </div>
          <div className="emp-form-actions">
            <button className="backbutton" onClick={() => navigate('/candidate/employment-info')}>Back</button>
            <button type="submit" className="stepHighSchoolButton" disabled={loading}>
              Save Details
            </button>
          </div>
        </form>
        <PopupModel
          show={showResultPopup}
          onClose={closeResultPopup}
          title={submissionResult?.title}
          message={submissionResult?.message}
          isSuccess={submissionResult?.isSuccess}
        />
      </Box>
    </>
  );
};

export default EmployeeDetailsForm;